import { useCallback, useEffect, useState } from 'react';

import { Corridor } from '@/components/cave/Corridor';
import { Hud } from '@/components/hud/Hud';
import { LootCard } from '@/components/loot/LootCard';
import { Icon, type IconName } from '@/components/ui/Icon';
import { useRun } from '@/store/run';
import { Achievements } from './Achievements';
import { Bag } from './Bag';
import { Book } from './Book';
import { Functions } from './Functions';
import { Heroes } from './Heroes';
import { Leaderboard } from './Leaderboard';
import { Maps } from './Maps';
import { Menu } from './Menu';
import { Missions } from './Missions';
import { Profile } from './Profile';
import { RewardHub } from './RewardHub';
import { Settings } from './Settings';
import { Shop } from './Shop';
import { Withdraw } from './Withdraw';

/** Screens on the bottom nav. */
export type Tab = 'menu' | 'dig' | 'bag' | 'hero' | 'shop' | 'book';

/** Every screen, including the seven reached through Functions. */
export type Screen =
  | Tab
  | 'functions' | 'rewardhub' | 'withdraw' | 'maps'
  | 'missions' | 'achievements' | 'leaderboard' | 'profile' | 'settings';

const TABS: { id: Tab; icon: IconName; label: string }[] = [
  { id: 'menu', icon: 'home', label: 'HOME' },
  { id: 'dig', icon: 'pick', label: 'DIG' },
  { id: 'bag', icon: 'bag', label: 'BAG' },
  { id: 'hero', icon: 'hero', label: 'HERO' },
  { id: 'shop', icon: 'shop', label: 'SHOP' },
  { id: 'book', icon: 'book', label: 'BOOK' },
];

const TAB_IDS = new Set<string>(TABS.map((t) => t.id));

/**
 * The phone frame, the six-tab nav, and routing for the meta screens.
 *
 * The HUD hides inside the tunnel: the tunnel carries its own depth, torch and
 * energy read-outs, and two stacked HUDs on a 390px-tall viewport leaves no cave.
 */
export function Shell() {
  const [screen, setScreen] = useState<Screen>('menu');
  /* Where a meta screen's back button returns to. Functions is reached from the
     menu, so a plain "go back to menu" would strand the player mid-flow. */
  const [backTo, setBackTo] = useState<Screen>('menu');

  const loot = useRun((s) => s.loot);
  const lootPending = useRun((s) => s.lootPending);
  const resume = useRun((s) => s.resume);
  const inCave = useRun((s) => s.phase !== 'idle');

  useEffect(() => {
    void resume().then(() => {
      if (useRun.getState().phase !== 'idle') setScreen('dig');
    });
  }, [resume]);

  const goto = useCallback((next: Screen, from: Screen = 'functions') => {
    setBackTo(from);
    setScreen(next);
  }, []);

  const openFunctions = useCallback(() => {
    setBackTo('menu');
    setScreen('functions');
  }, []);

  return (
    <div className="relative mx-auto flex h-full w-full max-w-[520px] flex-col overflow-hidden
                    bg-[radial-gradient(120%_70%_at_50%_-10%,#2A1B10_0%,transparent_60%)]
                    sm:my-auto sm:h-[min(100%,940px)] sm:rounded-[26px]">
      {screen !== 'dig' && <Hud />}

      <main className="relative min-h-0 flex-1 overflow-hidden">
        {screen === 'menu' && (
          <Menu
            onPlay={() => setScreen('dig')}
            onGo={(t) => setScreen(t)}
            onFunctions={openFunctions}
            onMaps={() => goto('maps', 'menu')}
          />
        )}
        {screen === 'dig' && (
          <Corridor
            onSurface={() => setScreen('menu')}
            onMaps={() => goto('maps', 'dig')}
          />
        )}
        {screen === 'bag' && <Bag />}
        {screen === 'hero' && <Heroes />}
        {screen === 'shop' && <Shop />}
        {screen === 'book' && <Book />}

        {screen === 'functions' && (
          <Functions onBack={() => setScreen('menu')} onGo={(s) => goto(s, 'functions')} />
        )}
        {screen === 'maps' && (
          <Maps onBack={() => setScreen(backTo)} onDig={() => setScreen('dig')} />
        )}
        {screen === 'rewardhub' && <RewardHub onBack={() => setScreen(backTo)} />}
        {screen === 'withdraw' && <Withdraw onBack={() => setScreen(backTo)} />}
        {screen === 'missions' && <Missions onBack={() => setScreen(backTo)} />}
        {screen === 'achievements' && <Achievements onBack={() => setScreen(backTo)} />}
        {screen === 'leaderboard' && <Leaderboard onBack={() => setScreen(backTo)} />}
        {screen === 'profile' && <Profile onBack={() => setScreen(backTo)} />}
        {screen === 'settings' && <Settings onBack={() => setScreen(backTo)} />}
      </main>

      {/* Held back for the length of the knock-out, so the card arrives as the rock
          lands rather than over the top of it falling. `lootPending` is set the instant
          the server says broken and cleared by a timer in the store — the *phase* is
          already 'loot', so nothing can be swung at or stepped through in the meantime. */}
      {loot && !lootPending && <LootCard />}

      {/*
        The cave owns the whole screen.

        A tab bar under a game is an invitation to leave it, and every tap that
        leaves used to end the run. Inside the cave the only way out is the pause
        overlay, which keeps the descent alive and asks before surfacing.
      */}
      {screen !== 'dig' && (
      <nav
        className="relative z-20 grid grid-cols-6 gap-1 border-t border-[var(--hairline)]
                   bg-[linear-gradient(rgba(32,20,16,0),var(--ui-panel)_40%)]
                   px-2 pb-[calc(env(safe-area-inset-bottom)+8px)] pt-2"
        aria-label="Main navigation"
      >
        {TABS.map((t) => {
          const active = screen === t.id;
          /* A meta screen keeps HOME lit, since that is where its trail leads. */
          const implied = t.id === 'menu' && !TAB_IDS.has(screen);
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setScreen(t.id)}
              aria-current={active ? 'page' : undefined}
              className={`relative flex flex-col items-center gap-1 rounded-sm py-2 transition-colors
                ${active || implied ? 'bg-[rgba(255,179,71,.1)] text-torch-core' : 'text-ink-dim'}`}
            >
              {active && <span className="absolute inset-x-[24%] top-0 h-0.5 bg-coin" aria-hidden />}
              {/* Drawn, not emoji. `currentColor` means the icon lights with its tab
                  and dims with it, so the grayscale filter that used to beat emoji
                  into submission is gone — and ⛏️ / 🧑‍🔧 can no longer fall back to
                  their component glyphs on a platform without the composed form. */}
              <Icon name={t.icon} size={20} />
              <span className="font-pixel text-[7px] tracking-wide">{t.label}</span>
              {t.id === 'dig' && inCave && !active && (
                <span
                  className="absolute right-[22%] top-1.5 h-1.5 w-1.5 rounded-full bg-[var(--danger)]"
                  aria-label="descent in progress"
                />
              )}
            </button>
          );
        })}
      </nav>
      )}
    </div>
  );
}
